/**
 * - Allows users to manage HTTP headers for the API request.
 * - Displays default headers (read-only)
 * - Supports adding, editing, enabling/disabling, and deleting custom headers
 */

import { Box, TextField, Typography, Button, Checkbox, IconButton, Divider } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { HeaderParam } from './types';

interface HeadersTabProps {
  headers: HeaderParam[];
  setHeaders: React.Dispatch<React.SetStateAction<HeaderParam[]>>;
  viewMode: boolean;
}

const HeadersTab = ({ headers, setHeaders, viewMode }: HeadersTabProps) => {
  // Default headers that are always included in the request (read-only)
  const DEFAULT_HEADERS = [
    { name: 'Accept', value: '*/*' },
    { name: 'Host', value: '<Calculated at runtime>' },
  ];

  const handleUpdate = (index: number, field: keyof HeaderParam, val: string | boolean) => {
    if (viewMode) return;
    const updated = [...headers];
    updated[index] = { ...updated[index], [field]: val };
    setHeaders(updated);
  };

  const addNewHeader = () => {
    if (viewMode) return;
    setHeaders([...headers, { name: '', value: '', enabled: false }]);
  };

  const removeHeader = (index: number) => {
    if (viewMode) return;
    if (headers.length === 1) {
      setHeaders([{ name: '', value: '', enabled: false }]);
    } else {
      const updated = headers.filter((_, i) => i !== index);
      setHeaders(updated);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* DEFAULT HEADERS */}
      {DEFAULT_HEADERS.map((header) => (
        <Box
          key={header.name}
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
            width: '100%',
          }}
        >
          <Typography sx={{ flex: 3, textAlign: 'left', fontSize: '12px' }}>
            {header.name}
          </Typography>

          <Typography
            sx={{ flex: 3, textAlign: 'left', color: 'text.secondary', fontSize: '12px' }}
          >
            {header.value}
          </Typography>

          <Box sx={{ width: 70 }} />
        </Box>
      ))}

      <Divider sx={{ mb: 4 }} />

      {/* EDITABLE HEADERS */}
      {headers.map((header, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
            width: '100%',
          }}
        >
          <TextField
            label="name"
            size="small"
            sx={{ flex: 3, mr: 2 }}
            value={header.name}
            disabled={viewMode || !header.enabled}
            onChange={(e) => handleUpdate(index, 'name', e.target.value)}
          />

          <TextField
            label="value"
            size="small"
            sx={{ flex: 3, mr: 2 }}
            value={header.value}
            disabled={viewMode || !header.enabled}
            onChange={(e) => handleUpdate(index, 'value', e.target.value)}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              width: 70,
            }}
          >
            <Checkbox
              checked={header.enabled}
              disabled={viewMode}
              onChange={(e) => handleUpdate(index, 'enabled', e.target.checked)}
            />

            <DeleteIcon
              sx={{
                cursor: 'pointer',
                ml: 1,
                color: 'text.secondary',
                '&:hover': {
                  color: 'error.main',
                },
              }}
              onClick={() => removeHeader(index)}
            />
          </Box>
        </Box>
      ))}

      {/* ADD BUTTON */}
      {!viewMode && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
          <Button variant="outlined" onClick={addNewHeader}>
            + Add
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default HeadersTab;
